<template>
  <sp-button
    size="small"
    :variant="variant"
    no-wrap
    block
    :data-testid="TestId.slots.more"
    :aria-label="accessibleLabel"
    :data-analytics-element="analyticsElement"
    data-analytics-interaction="click"
    :data-analytics-value="day"
    @click="showMore"
  >
    {{ label }}
    <sp-icon slot="append" :name="icon" :size="iconSize"></sp-icon>
  </sp-button>
</template>

<script setup>
import { computed } from "vue";
import { i18n } from "#ce/components/SpFinder/i18n";
import { TestId } from "#/test-ids";
import { trackControlClick } from "#ce/components/SpFinder/lib/click-analytics";
import { toDayKey } from "./slot-format";

const props = defineProps({
  /**
   * How many slots the button stands for.
   *
   * @type {Number}
   */
  count: {
    type: Number,
    required: true,
  },
  /**
   * The day the slots belong to: a `"YYYY-MM-DD"` string or a Date.
   *
   * @type {String|Date}
   */
  date: {
    type: [String, Date],
    required: true,
  },
  /**
   * `sp-button` variant.
   *
   * @type {String}
   */
  variant: {
    type: String,
    default: "outlined",
  },
  /**
   * `sp-icon` name after the label.
   *
   * @type {String}
   */
  icon: {
    type: String,
    default: "triangle-down-filled",
  },
  /**
   * Accessible name, when the visible "N more" needs context such as its day.
   *
   * @type {String}
   */
  accessibleLabel: {
    type: String,
    default: undefined,
  },
  /**
   * `data-analytics-element` value.
   *
   * @type {String}
   */
  analyticsElement: {
    type: String,
    required: true,
  },
  /**
   * `sp-icon` size. `null` leaves the size to the `--sp-comp-button-*-icon-size` token.
   *
   * @type {String}
   */
  iconSize: {
    type: String,
    default: "small",
  },
});

const emit = defineEmits(["show-more"]);

const day = computed(() => toDayKey(props.date));
const label = computed(() => i18n.global.t("more_count", { count: props.count }));

function showMore(event) {
  trackControlClick(event);
  emit("show-more", { date: props.date });
}
</script>

<style scoped lang="scss">
sp-button {
  --sp-comp-button-font-weight: var(--sp-comp-slot-more-button-font-weight, var(--sp-sys-typescale-body-font-weight));
}

sp-icon {
  margin-inline: var(--sp-comp-slot-more-button-icon-margin-inline, 0);
}
</style>
